import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold.tsx';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_SERIES_SQUAD } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary.ts';
import { useCommonGameSet } from '@sorare/core/src/set';

import squadImg from '#src/assets/squad.webp';

import { SeriesBoardTimer } from '../SeriesBoardTimer/index.tsx';
import type {
  SeriesBoardTrckSwitcher_squad,
  SeriesBoardTrckSwitcher_tasksTrack,
} from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical)`
  position: relative;
`;

const IconWrapper = styled.div`
  &.open {
    transform: rotate(180deg);
  }
  transition: transform 0.3s ease-in-out;
`;

const Absolute = styled.div`
  position: absolute;
  top: calc(100% + var(--double-unit));
  left: 0;
  right: 0;
`;
type Props = {
  trck: SeriesBoardTrckSwitcher_tasksTrack;
  switcherOpen: boolean;
  onSwitcherToggle?: () => void;
  squad: Nullable<SeriesBoardTrckSwitcher_squad>;
};

export const SeriesBoardTrckSwitcher = ({
  trck,
  switcherOpen,
  onSwitcherToggle,
  squad,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { SetIcon } = useCommonGameSet().visual.brand;

  const individualTrack = (
    <Horizontal>
      <SetIcon size={5} />
      <Vertical gap={0.5}>
        <BrandS>{trck.title}</BrandS>
        {!!trck.endDate && <SeriesBoardTimer endDate={trck.endDate} />}
      </Vertical>
    </Horizontal>
  );
  const squadTrack = (
    <Horizontal>
      <img src={squadImg} width={40} alt="Squad Track" />
      <Vertical>
        <BrandS>
          {squad ? squad.name : <FormattedMessage {...glossary.squads} />}
        </BrandS>
      </Vertical>
    </Horizontal>
  );

  if (!onSwitcherToggle) {
    return <Wrapper gap={3}>{individualTrack}</Wrapper>;
  }

  return (
    <Wrapper gap={3}>
      <Horizontal
        onClick={onSwitcherToggle}
        as="button"
        style={{
          outline: 'none',
        }}
      >
        {individualTrack}
        <IconWrapper className={classNames({ open: switcherOpen })}>
          <ChevronDownBold />
        </IconWrapper>
      </Horizontal>
      {!!switcherOpen && (
        <Absolute
          as={Link}
          to={generateSportContextPath(ANY_SPORT_SERIES_SQUAD)}
        >
          {squadTrack}
        </Absolute>
      )}
    </Wrapper>
  );
};

SeriesBoardTrckSwitcher.fragments = {
  tasksTrack: gql`
    fragment SeriesBoardTrckSwitcher_tasksTrack on TasksTrack {
      id
      title
      endDate
    }
  ` as TypedDocumentNode<SeriesBoardTrckSwitcher_tasksTrack>,
  squad: gql`
    fragment SeriesBoardTrckSwitcher_squad on Squad {
      id
      name
    }
  ` as TypedDocumentNode<SeriesBoardTrckSwitcher_squad>,
};
